// Espace employé — shared vocabulary: service catalog, tones, doc
// workflow labels, ring/mini-chart visuals. Ported from the design
// prototype (es-docs.jsx / es-employe.jsx) and rewired onto the real
// API DTOs.
import type { CSSProperties } from 'react';
import type { DocRequestForme, DocRequestStatus, DocRequestType, LeaveType, MeLeaveStage, ClaimStage } from '../../api/hooks.js';

export type ServiceKey = 'conge' | 'recup' | 'jour' | 'attestation' | 'ats' | 'attconge' | 'paie' | 'autre';
export type ServiceKind = 'leave' | 'claim' | 'doc';
export type Tone = 'shf' | 'info' | 'warn' | 'green' | 'neutral';

export const ES_SERVICES: Record<ServiceKey, { lbl: string; sub: string; ic: string; kind: ServiceKind; tone: Tone; doc?: DocRequestType }> = {
  conge:       { lbl: 'Demander un congé', sub: 'Sur votre solde annuel', ic: '✈', kind: 'leave', tone: 'shf' },
  recup:       { lbl: 'Poser une récup', sub: 'Sur votre stock de récupération', ic: '↧', kind: 'leave', tone: 'info' },
  jour:        { lbl: 'Déclarer un jour travaillé', sub: 'Week-end ou férié → +1 j de récup', ic: '＋', kind: 'claim', tone: 'warn' },
  attestation: { lbl: 'Attestation de travail', sub: 'Poste, ancienneté, type de contrat', ic: '▤', kind: 'doc', tone: 'shf', doc: 'ATTESTATION_TRAVAIL' },
  ats:         { lbl: 'Attestation de salaire (ATS)', sub: 'Travail + salaire — banque, crédit, visa', ic: '≡', kind: 'doc', tone: 'info', doc: 'ATS' },
  attconge:    { lbl: 'Attestation de congé', sub: 'Preuve d’un congé validé — visa, école, banque', ic: '☷', kind: 'doc', tone: 'warn', doc: 'ATTESTATION_CONGE' },
  paie:        { lbl: 'Fiche de paie', sub: 'Demandez les mois qu’il vous faut à la RH', ic: '⌗', kind: 'doc', tone: 'green', doc: 'FICHE_PAIE' },
  autre:       { lbl: 'Autre document', sub: 'Attestation de stage, domiciliation…', ic: '⋯', kind: 'doc', tone: 'neutral', doc: 'AUTRE' },
};
export const DOC_SERVICE: Record<DocRequestType, ServiceKey> = {
  ATTESTATION_TRAVAIL: 'attestation', ATS: 'ats', ATTESTATION_CONGE: 'attconge', FICHE_PAIE: 'paie', AUTRE: 'autre',
};

export const ES_TONE: Record<Tone, [string, string]> = {
  shf: ['var(--shfs)', 'var(--shf)'],
  info: ['var(--infos)', 'var(--infoc)'],
  warn: ['var(--warns)', 'var(--warnc)'],
  green: ['var(--greens)', 'var(--greenc)'],
  neutral: ['var(--s2)', 'var(--ink2)'],
};

export const LEAVE_TYPE_LBL: Record<LeaveType, { lbl: string; short: string; c: string; bg: string }> = {
  CONGE: { lbl: 'Congé annuel', short: 'Congé', c: 'var(--shf)', bg: 'var(--shfs)' },
  RECUP: { lbl: 'Récupération', short: 'Récup', c: 'var(--infoc)', bg: 'var(--infos)' },
};

// stage → [label, chip class] for leave requests…
export const LEAVE_ST: Record<MeLeaveStage, [string, string]> = {
  PROPOSED_BY_LAMIS: ['En attente', 'warn'],
  APPROVED: ['Validée', 'green'],
  DEBITED: ['Validée', 'green'],
  REJECTED: ['Refusée', 'bad'],
};
// …and worked-day claims…
export const CLAIM_ST: Record<ClaimStage, [string, string]> = {
  PENDING: ['En attente', 'warn'],
  APPROVED: ['Créditée', 'green'],
  REJECTED: ['Refusée', 'bad'],
};
// …and document requests.
export const DOC_ST: Record<DocRequestStatus, [string, string]> = {
  RECUE: ['Reçue', 'warn'],
  PREPARATION: ['En préparation RH', 'warn'],
  SIGNATURE: ['En signature DG', 'info'],
  PRETE: ['Prête à retirer', 'green'],
  ENVOYEE: ['Envoyée par email', 'green'],
  REFUSEE: ['Refusée', 'bad'],
};
export const DOC_SLA: Record<DocRequestForme, string> = { SCAN: '24 h ouvrées', ORIGINAL: '48 h ouvrées' };
export const docFlow = (forme: DocRequestForme): DocRequestStatus[] =>
  forme === 'ORIGINAL' ? ['RECUE', 'PREPARATION', 'SIGNATURE', 'PRETE'] : ['RECUE', 'PREPARATION', 'ENVOYEE'];
export const DOC_FLOW_LBL: Record<DocRequestStatus, string> = {
  RECUE: 'Demande reçue', PREPARATION: 'Préparation RH', SIGNATURE: 'Signature du DG',
  PRETE: 'À retirer au siège', ENVOYEE: 'Envoyée par email', REFUSEE: 'Refusée',
};

// ── document workflow track (proto EsTrack) ──────────────────
export function EsTrack({ status, forme }: { status: DocRequestStatus; forme: DocRequestForme }) {
  const flow = docFlow(forme);
  const idx = flow.indexOf(status);
  return (
    <div className="es-track">
      {flow.map((s, i) => (
        <div key={s} className={'es-tstep' + (i < idx ? ' done' : '') + (i === idx ? ' cur' : '')}>
          <i />
          <span>{DOC_FLOW_LBL[s]}</span>
        </div>
      ))}
    </div>
  );
}

// ── progress ring (proto EsRing) ─────────────────────────────
export function EsRing({ value, total, color, size = 100 }: { value: number; total: number; color: string; size?: number }) {
  const pct = total <= 0 ? 0 : Math.max(0, Math.min(1, value / total));
  const r = size / 2 - 9;
  const c = 2 * Math.PI * r;
  return (
    <svg className="es-ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--s3)" strokeWidth="8" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ '--c': c, animation: 'es-ringIn 1.2s cubic-bezier(.2,.8,.2,1) both' } as CSSProperties} />
      <text x="50%" y="53%" textAnchor="middle" className="es-ring-t">{Math.round(pct * 100)}%</text>
    </svg>
  );
}

// ── mini balance curve (proto EsMiniChart) ───────────────────
export function EsMiniChart({ pts, color }: { pts: Array<{ v: number; future?: boolean }>; color: string }) {
  if (pts.length < 2) return null;
  const w = 260, h = 44;
  const vals = pts.map((p) => p.v);
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const x = (i: number) => i * (w / (pts.length - 1));
  const y = (v: number) => h - 3 - ((v - min) / ((max - min) || 1)) * (h - 8);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const now = pts.findIndex((p) => p.future) - 1;
  return (
    <svg className="es-mini" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <path d={`${line} L${w},${h} L0,${h} Z`} fill={color} opacity=".1" />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" className="es-mini-l" />
      {now >= 0 && pts[now] && <circle cx={x(now)} cy={y(pts[now].v)} r="3.5" fill={color} />}
    </svg>
  );
}

// Chart events (ledger) → 12 monthly balance points for the mini chart.
export function monthlyCurve(events: Array<{ date: string; balance: number; amount: number }>, year: number, fallback: number, todayIso: string) {
  const opening = events.length ? events[0]!.balance - events[0]!.amount : fallback;
  const pts: Array<{ v: number; future: boolean }> = [];
  let run = opening;
  for (let m = 1; m <= 12; m++) {
    const end = `${year}-${String(m).padStart(2, '0')}-31`;
    for (const e of events) if (e.date <= end && e.date.slice(0, 7) === `${year}-${String(m).padStart(2, '0')}`) run = e.balance;
    pts.push({ v: run, future: `${year}-${String(m).padStart(2, '0')}-01` > todayIso });
  }
  return pts;
}
